import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { brandAPI, organizationsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import {
    Building2,
    TrendingUp,
    TrendingDown,
    Minus,
    DollarSign,
    Star,
    Users,
    MapPin,
    Calendar,
    RefreshCw,
    Award,
    ChevronRight,
    Loader2,
    HelpCircle,
    ArrowLeftRight,
    Trophy,
    Target
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../components/ui/tooltip';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '../components/ui/dialog';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    ResponsiveContainer,
    Legend,
    Tooltip as RechartsTooltip,
    Cell
} from 'recharts';
import {
    InfoBanner,
    ViewOnlyBanner,
    FeatureIntro
} from '../components/UsabilityComponents';

// Help tooltip component
const HelpTooltip = ({ text }) => (
    <TooltipProvider>
        <Tooltip>
            <TooltipTrigger asChild>
                <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help inline-block ml-1" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
                <p className="text-sm">{text}</p>
            </TooltipContent>
        </Tooltip>
    </TooltipProvider>
);

// Format currency
const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(value);
};

// Format number with commas
const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value);
};

// Brand colors
const BRAND_COLORS = {
    a: '#10B981', // Emerald
    b: '#6366F1'  // Indigo
};

// Comparison stat card
const ComparisonStatCard = ({ label, brandAValue, brandBValue, format = 'number', icon: Icon }) => {
    const aVal = format === 'currency' ? parseFloat(brandAValue) : brandAValue;
    // Check for both null and undefined
    const bVal = (brandBValue !== null && brandBValue !== undefined) 
        ? (format === 'currency' ? parseFloat(brandBValue) : brandBValue) 
        : null;
    
    const diff = bVal !== null ? aVal - bVal : null;
    const isAWinning = diff === null || diff >= 0;
    
    const formatValue = (val) => {
        if (format === 'currency') return formatCurrency(val);
        if (format === 'percent') return `${val}%`;
        return formatNumber(val);
    };
    
    return (
        <div className="bg-background rounded-lg border p-4">
            <div className="flex items-center gap-2 mb-3 text-sm text-muted-foreground">
                {Icon && <Icon className="w-4 h-4" />}
                <span>{label}</span>
            </div>
            <div className="grid grid-cols-2 gap-4">
                <div className={`text-center p-3 rounded-lg ${isAWinning ? 'bg-emerald-50 ring-2 ring-emerald-200' : 'bg-slate-50 dark:bg-slate-800'}`}>
                    <p className={`text-2xl font-bold ${isAWinning ? 'text-emerald-600' : 'text-foreground'}`}>
                        {formatValue(aVal)}
                    </p>
                    {isAWinning && diff !== null && diff !== 0 && (
                        <Badge className="mt-1 bg-emerald-100 text-emerald-700 text-xs">
                            <TrendingUp className="w-3 h-3 mr-1" />
                            +{format === 'currency' ? formatCurrency(Math.abs(diff)) : formatNumber(Math.abs(diff))}
                        </Badge>
                    )}
                </div>
                {bVal !== null && (
                    <div className={`text-center p-3 rounded-lg ${!isAWinning ? 'bg-indigo-50 ring-2 ring-indigo-200' : 'bg-slate-50 dark:bg-slate-800'}`}>
                        <p className={`text-2xl font-bold ${!isAWinning ? 'text-indigo-600' : 'text-foreground'}`}>
                            {formatValue(bVal)}
                        </p>
                        {!isAWinning && diff !== 0 && (
                            <Badge className="mt-1 bg-indigo-100 text-indigo-700 text-xs">
                                <TrendingUp className="w-3 h-3 mr-1" />
                                +{format === 'currency' ? formatCurrency(Math.abs(diff)) : formatNumber(Math.abs(diff))}
                            </Badge>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default function BrandComparison() {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [brands, setBrands] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [brandA, setBrandA] = useState('');
    const [brandB, setBrandB] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [data, setData] = useState(null);
    const [showBrandDialog, setShowBrandDialog] = useState(false);
    const [selectedOrg, setSelectedOrg] = useState(null);
    const [newBrandName, setNewBrandName] = useState('');
    const [savingBrand, setSavingBrand] = useState(false);
    
    const { isSuperAdmin, isViewOnly, canEditBrands } = useAuth();

    useEffect(() => {
        // Set default dates (last 30 days)
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - 30);
        
        setEndDate(end.toISOString().split('T')[0]);
        setStartDate(start.toISOString().split('T')[0]);
        
        loadBrands();
        loadOrganizations();
    }, []);

    const loadBrands = async () => {
        try {
            const res = await brandAPI.getBrands();
            setBrands(res.data.brands || []);
            
            // Auto-select first brand if available
            if (res.data.brands?.length > 0) {
                setBrandA(res.data.brands[0].name);
                if (res.data.brands.length > 1) {
                    setBrandB(res.data.brands[1].name);
                }
            }
        } catch (error) {
            console.error('Failed to load brands');
        }
    };

    const loadOrganizations = async () => {
        try {
            const res = await organizationsAPI.list();
            setOrganizations(res.data || []);
        } catch (error) {
            console.error('Failed to load organizations');
        }
    };

    const loadComparison = async () => {
        if (!brandA) {
            toast.error('Please select at least one brand');
            return;
        }
        
        setLoading(true);
        try {
            const params = {
                brand_a: brandA,
                start_date: startDate,
                end_date: endDate
            };
            if (brandB && brandB !== brandA) {
                params.brand_b = brandB;
            }
            
            const res = await brandAPI.getComparison(params);
            setData(res.data);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to load comparison');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (brandA && startDate && endDate) {
            loadComparison();
        }
    }, [brandA, brandB, startDate, endDate]);

    const openBrandDialog = (org) => {
        setSelectedOrg(org);
        setNewBrandName(org.brand || '');
        setShowBrandDialog(true);
    };

    const saveBrand = async () => {
        if (!selectedOrg) return;
        
        setSavingBrand(true);
        try {
            await brandAPI.updateOrgBrand(selectedOrg.id, newBrandName.trim() || null);
            toast.success(`Brand updated for ${selectedOrg.name}`);
            setShowBrandDialog(false);
            loadBrands();
            loadOrganizations();
            loadComparison();
        } catch (error) {
            toast.error('Failed to update brand');
        } finally {
            setSavingBrand(false);
        }
    };

    // Prepare chart data
    const chartData = data?.brand_a && data?.brand_b ? [
        {
            name: 'Reviews',
            [data.brand_a.brand]: data.brand_a.total_reviews,
            [data.brand_b.brand]: data.brand_b.total_reviews
        },
        {
            name: 'Locations',
            [data.brand_a.brand]: data.brand_a.location_count,
            [data.brand_b.brand]: data.brand_b.location_count
        },
        {
            name: 'Organizations',
            [data.brand_a.brand]: data.brand_a.organization_count,
            [data.brand_b.brand]: data.brand_b.organization_count
        }
    ] : [];

    const noBrandsConfigured = brands.length === 0;

    return (
        <div className="space-y-6" data-testid="brand-comparison-page">
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Multi-Brand Comparison
                    </h1>
                    <p className="text-muted-foreground mt-1">{t('brandComparison.compareHint')}</p>
                </div>
                <Button variant="outline" onClick={loadComparison} disabled={loading || !brandA}>
                    <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                </Button>
            </div>

            {/* View Only Banner for Franchise Admin */}
            {isViewOnly() && (
                <ViewOnlyBanner 
                    role="Franchise Admin"
                    message="You have view-only access to brand comparisons across your assigned organizations."
                />
            )}

            {/* No Brands Warning */}
            {noBrandsConfigured && !isViewOnly() && (
                <Card className="border-amber-200 bg-amber-50">
                    <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                                <Building2 className="w-6 h-6 text-amber-600" />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-semibold text-amber-900 mb-2">{t('brandComparison.noBrandsConfigured')}</h3>
                                <p className="text-amber-700 mb-4">
                                    To use Multi-Brand Comparison, you need to assign brands to your organizations.
                                    A brand is a parent company that owns multiple restaurant concepts.
                                </p>
                                <p className="text-amber-700 mb-4">
                                    <strong>Example:</strong> &quot;Foodtastic&quot; (brand) owns &quot;Lucille&apos;s&quot;, &quot;Second Cup&quot;, &quot;Pita Pit&quot; (organizations)
                                </p>
                                <p className="text-sm text-amber-600">
                                    Click on any organization below to assign it to a brand.
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Brand Selector & Date Range */}
            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-wrap items-end gap-4">
                        <div className="min-w-[200px]">
                            <Label className="text-sm mb-1 block">
                                Brand A
                                <HelpTooltip text="Select the first brand to analyze or compare" />
                            </Label>
                            <Select value={brandA} onValueChange={setBrandA} disabled={brands.length === 0}>
                                <SelectTrigger className="border-emerald-300 focus:ring-emerald-500">
                                    <SelectValue placeholder="Select brand..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {brands.map(b => (
                                        <SelectItem key={b.name} value={b.name}>
                                            <span className="flex items-center gap-2">
                                                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                                                {b.name} ({b.organization_count} orgs)
                                            </span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        
                        <div className="flex items-center justify-center w-10 h-10">
                            <ArrowLeftRight className="w-5 h-5 text-muted-foreground" />
                        </div>
                        
                        <div className="min-w-[200px]">
                            <Label className="text-sm mb-1 block">
                                Brand B (Optional)
                                <HelpTooltip text="Select a second brand to compare against Brand A" />
                            </Label>
                            <Select value={brandB || "none"} onValueChange={(val) => setBrandB(val === "none" ? "" : val)} disabled={brands.length < 2}>
                                <SelectTrigger className="border-indigo-300 focus:ring-indigo-500">
                                    <SelectValue placeholder="Select brand..." />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">None (Single Brand View)</SelectItem>
                                    {brands.filter(b => b.name !== brandA).map(b => (
                                        <SelectItem key={b.name} value={b.name}>
                                            <span className="flex items-center gap-2">
                                                <div className="w-2 h-2 rounded-full bg-indigo-500" />
                                                {b.name} ({b.organization_count} orgs)
                                            </span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        
                        <div className="h-10 w-px bg-slate-200 dark:bg-slate-600" />
                        
                        <div>
                            <Label className="text-sm mb-1 block">Start Date</Label>
                            <Input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="w-40"
                            />
                        </div>
                        <div>
                            <Label className="text-sm mb-1 block">End Date</Label>
                            <Input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="w-40"
                            />
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Loading State */}
            {loading && (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
                </div>
            )}

            {/* Comparison Results */}
            {!loading && data?.brand_a && (
                <>
                    {/* Brand Headers */}
                    <div className="grid grid-cols-2 gap-4">
                        <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
                            <CardContent className="p-5">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-xl bg-emerald-500 flex items-center justify-center">
                                        <Building2 className="w-6 h-6 text-white" />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-bold text-emerald-800">{data.brand_a.brand}</h2>
                                        <p className="text-sm text-emerald-600">
                                            {data.brand_a.organization_count} organizations • {data.brand_a.location_count} locations
                                        </p>
                                    </div>
                                    {data.comparison?.leader === data.brand_a.brand && (
                                        <Badge className="ml-auto bg-emerald-100 text-emerald-700">
                                            <Trophy className="w-3 h-3 mr-1" />
                                            Leading
                                        </Badge>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                        
                        {data.brand_b ? (
                            <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-white">
                                <CardContent className="p-5">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 rounded-xl bg-indigo-500 flex items-center justify-center">
                                            <Building2 className="w-6 h-6 text-white" />
                                        </div>
                                        <div>
                                            <h2 className="text-xl font-bold text-indigo-800">{data.brand_b.brand}</h2>
                                            <p className="text-sm text-indigo-600">
                                                {data.brand_b.organization_count} organizations • {data.brand_b.location_count} locations
                                            </p>
                                        </div>
                                        {data.comparison?.leader === data.brand_b.brand && (
                                            <Badge className="ml-auto bg-indigo-100 text-indigo-700">
                                                <Trophy className="w-3 h-3 mr-1" />
                                                Leading
                                            </Badge>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        ) : (
                            <Card className="border-dashed border-2 border-slate-200 bg-slate-50 dark:bg-slate-800">
                                <CardContent className="p-5 flex items-center justify-center">
                                    <p className="text-muted-foreground">{t('brandComparison.selectBrandB')}</p>
                                </CardContent>
                            </Card>
                        )}
                    </div>

                    {/* Comparison Stats */}
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <ComparisonStatCard
                            label="Total Reviews"
                            brandAValue={data.brand_a.total_reviews}
                            brandBValue={data.brand_b?.total_reviews}
                            icon={Star}
                        />
                        <ComparisonStatCard
                            label="Total ROI"
                            brandAValue={data.brand_a.total_roi}
                            brandBValue={data.brand_b?.total_roi}
                            format="currency"
                            icon={DollarSign}
                        />
                        <ComparisonStatCard
                            label="Avg Daily Velocity"
                            brandAValue={data.brand_a.avg_daily_velocity}
                            brandBValue={data.brand_b?.avg_daily_velocity}
                            icon={TrendingUp}
                        />
                        <ComparisonStatCard
                            label="Staff Adoption"
                            brandAValue={data.brand_a.staff_adoption.rate}
                            brandBValue={data.brand_b?.staff_adoption?.rate}
                            format="percent"
                            icon={Users}
                        />
                    </div>

                    {/* Chart (only if comparing) */}
                    {data.brand_b && (
                        <Card data-insight-id="reporting.workflow.brand-comparison" data-insight-label="Side-by-side brand comparison">
                            <CardHeader>
                                <CardTitle>Side-by-Side Comparison</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="h-64">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={chartData} layout="vertical">
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis type="number" />
                                            <YAxis dataKey="name" type="category" width={100} />
                                            <RechartsTooltip />
                                            <Legend />
                                            <Bar dataKey={data.brand_a.brand} fill={BRAND_COLORS.a} radius={[0, 4, 4, 0]} />
                                            <Bar dataKey={data.brand_b.brand} fill={BRAND_COLORS.b} radius={[0, 4, 4, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Top Locations per Brand */}
                    <div className="grid lg:grid-cols-2 gap-6">
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="flex items-center gap-2 text-lg">
                                    <Award className="w-5 h-5 text-emerald-500" />
                                    Top Locations - {data.brand_a.brand}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {data.brand_a.top_locations.length > 0 ? (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead className="w-10">#</TableHead>
                                                <TableHead>Location</TableHead>
                                                <TableHead className="text-right">Reviews</TableHead>
                                                <TableHead className="text-right">ROI</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {data.brand_a.top_locations.map((loc, i) => (
                                                <TableRow key={loc.location_id}>
                                                    <TableCell>
                                                        <Badge className={i === 0 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 dark:bg-slate-700 text-muted-foreground'}>
                                                            {i + 1}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div>
                                                            <p className="font-medium">{loc.location_name}</p>
                                                            <p className="text-xs text-muted-foreground">{loc.organization_name}</p>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="text-right text-emerald-600 font-semibold">
                                                        {formatNumber(loc.reviews)}
                                                    </TableCell>
                                                    <TableCell className="text-right text-emerald-600 font-semibold">
                                                        {formatCurrency(loc.roi_value)}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                ) : (
                                    <p className="text-muted-foreground text-center py-6">{t('brandComparison.noDataForPeriod')}</p>
                                )}
                            </CardContent>
                        </Card>

                        {data.brand_b ? (
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle className="flex items-center gap-2 text-lg">
                                        <Award className="w-5 h-5 text-indigo-500" />
                                        Top Locations - {data.brand_b.brand}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {data.brand_b.top_locations.length > 0 ? (
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead className="w-10">#</TableHead>
                                                    <TableHead>Location</TableHead>
                                                    <TableHead className="text-right">Reviews</TableHead>
                                                    <TableHead className="text-right">ROI</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {data.brand_b.top_locations.map((loc, i) => (
                                                    <TableRow key={loc.location_id}>
                                                        <TableCell>
                                                            <Badge className={i === 0 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 dark:bg-slate-700 text-muted-foreground'}>
                                                                {i + 1}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell>
                                                            <div>
                                                                <p className="font-medium">{loc.location_name}</p>
                                                                <p className="text-xs text-muted-foreground">{loc.organization_name}</p>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="text-right text-indigo-600 font-semibold">
                                                            {formatNumber(loc.reviews)}
                                                        </TableCell>
                                                        <TableCell className="text-right text-indigo-600 font-semibold">
                                                            {formatCurrency(loc.roi_value)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    ) : (
                                        <p className="text-muted-foreground text-center py-6">{t('brandComparison.noDataForPeriod')}</p>
                                    )}
                                </CardContent>
                            </Card>
                        ) : (
                            <Card className="border-dashed border-2 border-slate-200 bg-slate-50 dark:bg-slate-800 flex items-center justify-center">
                                <CardContent className="text-center py-12">
                                    <ArrowLeftRight className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                                    <p className="text-muted-foreground">{t('brandComparison.selectBrandBSee')}</p>
                                </CardContent>
                            </Card>
                        )}
                    </div>

                    {/* Organizations in each brand */}
                    <Card>
                        <CardHeader>
                            <CardTitle>{t('brandComparison.organizationsByBrand')}</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid lg:grid-cols-2 gap-6">
                                <div>
                                    <h4 className="font-semibold text-emerald-700 mb-3 flex items-center gap-2">
                                        <div className="w-3 h-3 rounded-full bg-emerald-500" />
                                        {data.brand_a.brand}
                                    </h4>
                                    <div className="space-y-2">
                                        {data.brand_a.organizations.map(org => (
                                            <div key={org.id} className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                                <span className="font-medium">{org.name}</span>
                                                <ChevronRight className="w-4 h-4 text-muted-foreground" />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                {data.brand_b && (
                                    <div>
                                        <h4 className="font-semibold text-indigo-700 mb-3 flex items-center gap-2">
                                            <div className="w-3 h-3 rounded-full bg-indigo-500" />
                                            {data.brand_b.brand}
                                        </h4>
                                        <div className="space-y-2">
                                            {data.brand_b.organizations.map(org => (
                                                <div key={org.id} className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                                    <span className="font-medium">{org.name}</span>
                                                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </>
            )}

            {/* Organization Brand Management - Super Admin only */}
            {canEditBrands() && (
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Building2 className="w-5 h-5 text-muted-foreground" />
                            Manage Organization Brands
                            <HelpTooltip text="Assign organizations to brands for multi-brand comparison. Click an organization to set its brand." />
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {organizations.map(org => (
                                <div
                                    key={org.id}
                                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 dark:bg-slate-800 cursor-pointer transition-colors"
                                    onClick={() => openBrandDialog(org)}
                                    data-testid={`org-brand-${org.id}`}
                                >
                                    <div>
                                        <p className="font-medium">{org.name}</p>
                                        {org.brand ? (
                                            <Badge className="mt-1 bg-emerald-100 text-emerald-700">
                                                {org.brand}
                                            </Badge>
                                        ) : (
                                            <Badge variant="outline" className="mt-1 text-muted-foreground">
                                                No brand
                                            </Badge>
                                        )}
                                    </div>
                                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Brand Assignment Dialog */}
            <Dialog open={showBrandDialog} onOpenChange={setShowBrandDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Assign Brand to {selectedOrg?.name}</DialogTitle>
                        <DialogDescription>
                            Enter the parent brand name for this organization. Organizations with the same brand name will be grouped together in comparisons.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <Label className="mb-2 block">Brand Name</Label>
                        <Input
                            value={newBrandName}
                            onChange={(e) => setNewBrandName(e.target.value)}
                            placeholder="e.g., Foodtastic, Dine Brands"
                            data-testid="brand-name-input"
                        />
                        <p className="text-sm text-muted-foreground mt-2">
                            Leave empty to remove from any brand grouping.
                        </p>
                        {brands.length > 0 && (
                            <div className="mt-4">
                                <p className="text-sm text-muted-foreground mb-2">Existing brands:</p>
                                <div className="flex flex-wrap gap-2">
                                    {brands.map(b => (
                                        <Badge
                                            key={b.name}
                                            className="cursor-pointer hover:bg-emerald-200"
                                            onClick={() => setNewBrandName(b.name)}
                                        >
                                            {b.name}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowBrandDialog(false)}>
                            Cancel
                        </Button>
                        <Button onClick={saveBrand} disabled={savingBrand}>
                            {savingBrand && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Save Brand
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
